import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import MediaQuery from 'react-responsive';
import { translate } from 'react-i18next';
import Spin from 'antd/lib/spin';
import {
  fetchAnonymizedLoansActions,
  selectLoans,
  selectLoansPagination,
  selectLoansFilter,
  setLoansFilterAction,
} from '../../../features/loans/ducks';
import config from '../../../config';
import { loanStatus, allocationType, settingKeys } from '../../../common/enums';
import { selectAccountBalance, selectAccountId } from '../../../features/auth/ducks';
import { selectIsInProgress } from '../../../features/spinner/ducks';
import { getPaymentTotal } from '../../../common/utils/mathHelpers';
import { CreateManualAllocationDesktop, CreateManualAllocationMobile } from '../components';
import { apiCallIds } from '../api';
import { createAllocationActions, selectAllocations } from '../ducks';
import { selectSetting, selectSettingNumeric } from '../../../features/settings/ducks';
import SignAllocation from '../components/SignAllocation';
import ConfirmAllocation from '../components/ConfirmAllocation';

const mapStateToProps = state => ({
  accountBalance: selectAccountBalance(state),
  accountId: selectAccountId(state),
  isCreating: selectIsInProgress(state, apiCallIds.CREATE_ALLOCATION),
  isFetchingLoans: selectIsInProgress(state, apiCallIds.FETCH_ALLOCATIONS),
  minMonths: selectSettingNumeric(state, settingKeys.LOAN_MIN_MONTHS),
  maxMonths: selectSettingNumeric(state, settingKeys.LOAN_MAX_MONTHS),
  loans: selectLoans(state),
  pagination: selectLoansPagination(state),
  filter: selectLoansFilter(state),
  allocation: selectAllocations(state)
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      createAllocation: createAllocationActions.request,
      setFilter: setLoansFilterAction,
      fetchAnonymizedLoans: fetchAnonymizedLoansActions.request,
    },
    dispatch
  ),
});

const defaultFilter = {
  status: loanStatus.SCH,
  termMonthsMin: '',
  termMonthsMax: '',
  ...config.defaultManualAllocationFilterValues,
};

@translate()
@connect(mapStateToProps, mapDispatchToProps)
export default class CreateManualAllocationContainer extends Component {
  static propTypes = {
    accountBalance: PropTypes.number.isRequired,
    accountId: PropTypes.string.isRequired,
    actions: PropTypes.object.isRequired,
    isCreating: PropTypes.bool.isRequired,
    isFetchingLoans: PropTypes.bool.isRequired,
    loans: PropTypes.array.isRequired,
    pagination: PropTypes.object.isRequired,
    t: PropTypes.func.isRequired,
    filter: PropTypes.object.isRequired,
  };

  state = {
    expandedRowKeys: [],
    expectedRevenues: {},
  };

  componentDidMount() {
    this.onResetFilters();
  }

  fetchAnonymizedLoans = (pagination, append) => {
    const { actions, filter } = this.props;
    actions.fetchAnonymizedLoans({
      append,
      options: {
        ...filter,
        pageNo: pagination.current - 1,
      },
    });
  };

  fetchAndAppend = () => {
    const { pagination } = this.props;
    this.fetchAnonymizedLoans({ ...pagination, current: pagination.current + 1 }, true);
  };

  fetchAndReplace = pagination => this.fetchAnonymizedLoans(pagination, false);

  onAllocationAmountChange = (amount, months, interest, loanId) => {
    this.setState(prevState => ({
      expectedRevenues: {
        ...prevState.expectedRevenues,
        [loanId]: getPaymentTotal(amount, months, interest),
      },
    }));
  };

  onRowClick = loan => {
    this.setState(prevState => {
      const idx = prevState.expandedRowKeys.indexOf(loan.id);
      if (idx === -1) {
        return {
          ...prevState,
          expandedRowKeys: [...prevState.expandedRowKeys, loan.id],
          expectedRevenues: {
            ...prevState.expectedRevenues,
            [loan.id]: undefined,
          },
        };
      }
      return {
        ...prevState,
        expandedRowKeys: [
          ...prevState.expandedRowKeys.slice(0, idx),
          ...prevState.expandedRowKeys.slice(idx + 1),
        ],
        expectedRevenues: {
          ...prevState.expectedRevenues,
          [loan.id]: undefined,
        },
      };
    });
  };

  onSubmit = allocationData => {
    const { accountId, actions, pagination } = this.props;

    actions.createAllocation({
      allocation: {
        ...allocationData,
        allocationType: allocationType.MANUAL,
      },
      accountId,
    });
  };

  validateAndSubmit = (loan, form) => {
    form.validateFields({ force: true }, (err, values) => {
      if (
        !err ||
        ((!err.amount || !err.amount[loan.id]))
      ) {
        const newAllocation = {
          amount: parseFloat(values.amount[loan.id]),
          loanId: loan.id,
          termMonths: loan.months,
        };
        this.onSubmit(newAllocation);
      }
    });
  };

  onFilterChange = (filterKey, filterValue) => {
    const { filter, actions } = this.props;
    let newFilter = {};

    if (typeof filterKey === 'object') {
      newFilter = {
        ...filter,
        ...filterKey,
      };
    } else {
      newFilter = {
        ...filter,
        [filterKey]: filterValue,
      };
    }

    actions.setFilter({
      append: false,
      options: newFilter,
    });
  };

  onResetFilters = () => {
    this.props.actions.setFilter({
      append: false,
      options: {
        ...defaultFilter,
      },
    });
  };

  render() {
    const {
      accountBalance,
      isCreating,
      isFetchingLoans,
      loans,
      filter,
      pagination,
      t,
      minMonths,
      maxMonths
    } = this.props;

    const { expandedRowKeys, expectedRevenues } = this.state;

    const formProps = {
      accountBalance,
      expandedRowKeys,
      expectedRevenues,
      loans: loans.filter(x => Number(x.remainingAmount) > 0),
      filter,
      onAllocationAmountChange: this.onAllocationAmountChange,
      onRowClick: this.onRowClick,
      validateAndSubmit: this.validateAndSubmit,
      onFilterChange: this.onFilterChange,
      onResetFilters: this.onResetFilters,
    };

    return (
      <section className="manual-allocation-wrapper">
        <h2>{t('Ponuka príležitostí')}</h2>
        <Spin spinning={isCreating || isFetchingLoans}>
          <SignAllocation />
          {/* <ConfirmAllocation isAllocation /> */}
          <MediaQuery maxWidth="767px">
            {matches =>
              matches ? (
                <CreateManualAllocationMobile
                  {...formProps}
                  fetchAndAppend={this.fetchAndAppend}
                  totalLoans={pagination.total}
                  minMonths={minMonths}
                  maxMonths={maxMonths}
                />
              ) : (
                <CreateManualAllocationDesktop
                  {...formProps}
                  onTableChange={this.fetchAndReplace}
                  pagination={pagination}
                  minMonths={minMonths}
                  maxMonths={maxMonths}
                />
              )
            }
          </MediaQuery>
        </Spin>
      </section>
    );
  }
}



// WEBPACK FOOTER //
// ./src/features/allocations/containers/CreateManualAllocationContainer.js